import React from 'react';
import { ConfirmationModal, Typography, useOverlayContext } from 'seagull';
import MissingSignaturesRecipients from "../components/MissingSignaturesRecipients/MissingSignaturesRecipients";
import RestfulWrapper from "../components/RestfulWrapper/RestfulWrapper";
import { useEventReporter, useStoreActions } from "../contexts";
import { Intent } from "../infra/access-control";
import { useSendReminder } from "./useSendReminder";
import { useWithdrawToEditProposal } from "./useWithdrawToEditProposal";
import { handlePromise } from "../infra/utils";
import { ErrorText, routes } from "../constants";
import { useHistory } from 'react-router';
import { useDuplicateRelationship } from "./useDuplicateRelationship";
import { useCopyProposalLink } from "./useCopyProposalLink";
import { useTerminateAgreement } from "./useTerminateAgreement";
const MissingSignaturesReminderModal = _ref => {
  let {
    relationship,
    dismissModal
  } = _ref;
  const {
    sendReminder
  } = useSendReminder();
  return /*#__PURE__*/React.createElement(ConfirmationModal, {
    textAlignCenter: false,
    title: "Send reminder",
    primaryButtonLabel: "Send now",
    primaryButtonTestid: "send-propposal-approval-reminder-button",
    secondaryButtonLabel: "Back",
    secondaryButtonTestid: "cancel-send-propposal-approval-reminder-button",
    onCloseButtonClick: dismissModal,
    onPrimaryButtonClick: () => {
      sendReminder(relationship);
      dismissModal();
    },
    onSecondaryButtonClick: dismissModal,
    content: /*#__PURE__*/React.createElement("div", {
      "data-testid": "send-proposal-approval-reminder-modal"
    }, /*#__PURE__*/React.createElement(Typography, null, "An email reminder will be sent to recipients who need to sign:"), /*#__PURE__*/React.createElement(MissingSignaturesRecipients, {
      relationship: relationship
    }))
  });
};
export function useProposalActions() {
  const {
    showModal,
    showErrorToast
  } = useOverlayContext();
  const {
    api
  } = useStoreActions();
  const {
    duplicateRelationshipAndRedirect
  } = useDuplicateRelationship();
  const {
    withdrawToEditProposalByIdWithConfirmation,
    withdrawToEditProposalById
  } = useWithdrawToEditProposal();
  const {
    reportToGtm,
    reportToAppcues
  } = useEventReporter();
  const {
    copyAgreementLink
  } = useCopyProposalLink();
  const {
    cancelProposalById
  } = useTerminateAgreement();
  const history = useHistory();
  return {
    getEditProposalAction(proposal, origin) {
      const fromProposalDashboard = origin === 'proposalsDashboard';
      return {
        text: fromProposalDashboard ? 'Edit' : 'Edit proposal',
        intent: Intent.EditProposal,
        disabled: proposal.isSample,
        onClick: async () => {
          if (fromProposalDashboard) {
            withdrawToEditProposalByIdWithConfirmation(proposal.id);
          } else {
            const confirmed = await showModal({
              closeOnClickOutside: true,
              closeOnEscapeKey: true,
              content: _ref2 => {
                let {
                  dismissModal
                } = _ref2;
                return /*#__PURE__*/React.createElement(ConfirmationModal, {
                  textAlignCenter: false,
                  skin: "danger",
                  title: "Withdraw to edit",
                  content: "To edit this proposal's details, it is necessary to withdraw it first. As soon as you send the new proposal, an email will be sent with a link to the new version.",
                  primaryButtonLabel: "Withdraw and edit",
                  primaryButtonTestid: "confirm-withdraw-to-edit",
                  secondaryButtonLabel: "Cancel",
                  onPrimaryButtonClick: () => dismissModal(true),
                  onSecondaryButtonClick: () => dismissModal(false)
                });
              }
            });
            if (!confirmed) return;
            const result = await handlePromise(withdrawToEditProposalById(proposal.id, {
              onDuplicateRelationshipError: () => showErrorToast(ErrorText.UnexpectedErrorTryAgain, 'duplicate-proposal-failed')
            }));
            if (result.success) {
              history.push(routes.draft(result.data));
            }
          }
        }
      };
    },
    getSendProposalReminderAction(proposalId, isSampleProposal) {
      return {
        text: 'Send reminder',
        intent: Intent.SendProposalReminder,
        disabled: isSampleProposal,
        onClick: () => {
          showModal({
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: _ref3 => {
              let {
                dismissModal
              } = _ref3;
              return /*#__PURE__*/React.createElement(RestfulWrapper, {
                fetch: () => api.relationship.get(proposalId),
                renderChild: _ref4 => {
                  let {
                    data
                  } = _ref4;
                  return /*#__PURE__*/React.createElement(MissingSignaturesReminderModal, {
                    dismissModal: dismissModal,
                    relationship: data
                  });
                }
              });
            }
          });
        }
      };
    },
    getDuplicateProposalAction(proposalId, isSampleProposal) {
      return {
        text: 'Duplicate',
        intent: Intent.DuplicateProposal,
        disabled: isSampleProposal,
        onClick: () => duplicateRelationshipAndRedirect(proposalId, 'duplicate-proposal')
      };
    },
    getCopyProposalLinkAction(proposal) {
      return {
        text: 'Copy link',
        intent: Intent.CopyRelationshipDirectLink,
        disabled: false,
        onClick: () => {
          reportToGtm('button-click', 'vendor-copy-link-proposal', {
            progressStatus: proposal.progressStatus
          });
          reportToAppcues('button-click', 'vendor-copy-link-proposal');
          copyAgreementLink({
            agreementId: proposal.id,
            vendorId: proposal.vendor.id,
            customerId: proposal.customer.id,
            progressStatus: proposal.progressStatus,
            withGlobalLoader: true
          });
        }
      };
    },
    getCancelProposalAction(proposalId, onSuccess) {
      return {
        text: 'Withdraw',
        disabled: false,
        intent: Intent.CancelProposal,
        onClick: () => cancelProposalById(proposalId, onSuccess)
      };
    }
  };
}